import {
  Box,
  FormControl,
  lighten,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { daDK } from '@mui/x-data-grid/locales';
import { DateTime } from 'luxon';
import React from 'react';

import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HdrAutoRoundedIcon from '@mui/icons-material/HdrAutoRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import NotificationsOffRoundedIcon from '@mui/icons-material/NotificationsOffRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';

import AcUnitRoundedIcon from '@mui/icons-material/AcUnitRounded';
import AvTimerRoundedIcon from '@mui/icons-material/AvTimerRounded';
import BatteryCharging20RoundedIcon from '@mui/icons-material/BatteryCharging20Rounded';
import DataThresholdingRoundedIcon from '@mui/icons-material/DataThresholdingRounded';
import DeviceThermostatRoundedIcon from '@mui/icons-material/DeviceThermostatRounded';
import ElectricalServicesRoundedIcon from '@mui/icons-material/ElectricalServicesRounded';
import HeatPumpRoundedIcon from '@mui/icons-material/HeatPumpRounded';
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded';
import HvacRoundedIcon from '@mui/icons-material/HvacRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SensorsOffRoundedIcon from '@mui/icons-material/SensorsOffRounded';
import WaterDamageRoundedIcon from '@mui/icons-material/WaterDamageRounded';

import { useLocalStorageJson } from '@/hooks/useLocalStorageJson';

import ViewAlarmDialog from '../dialog/ViewAlarmDialog';

import { useGridApiRef } from '@mui/x-data-grid-premium';

import AlarmDataGridToolbar from './AlarmDataGridToolbar';

function formatDanishDate(timestamp) {
  if (!timestamp) return '';
  return DateTime.fromISO(timestamp, { zone: 'utc' })
    .setLocale('da')
    .toFormat('dd. MMM yyyy HH:mm:ss');
}

const getSeverityIcon = (severity, theme) => {
  const isLight = theme.palette.mode === 'light';

  const errorColor = lighten(
    isLight ? theme.palette.error.light : theme.palette.error.dark,
    0.9,
  );
  const warningColor = lighten(
    isLight ? theme.palette.warning.light : theme.palette.warning.dark,
    0.9,
  );
  const infoColor = lighten(
    isLight ? theme.palette.info.light : theme.palette.info.dark,
    0.9,
  );

  switch (severity) {
    case 'Critical':
    case 'Major':
      return <ErrorRoundedIcon sx={{ color: errorColor }} />;
    case 'Medium':
    case 'Minor':
      return <WarningRoundedIcon sx={{ color: warningColor }} />;
    case 'Trivial':
    case 'Information':
      return <InfoRoundedIcon sx={{ color: infoColor }} />;
    default:
      return null;
  }
};

const alarmStates = [
  { value: 0, label: 'Unresolved' },
  { value: 1, label: 'False Alarm' },
  { value: 3, label: 'Solved' },
  { value: 4, label: 'Reset' },
  { value: 5, label: 'AutoResolved' },
];

// Create a lookup object to map string states to numeric values
const alarmStateValueMap = {
  Unresolved: 0,
  Reject: 1,
  Solved: 3,
  Reset: 4,
  AutoResolved: 5,
};

const getAlarmStateIcon = (state) => {
  const iconMap = {
    0: <NotificationsActiveRoundedIcon sx={{ color: 'text.primary', mr: 1 }} />, // Unresolved
    1: <NotificationsOffRoundedIcon sx={{ color: 'text.primary', mr: 1 }} />, // Reject
    3: <CheckCircleRoundedIcon sx={{ color: 'text.primary', mr: 1 }} />, // Solved
    4: <UndoRoundedIcon sx={{ color: 'text.primary', mr: 1 }} />, // Reset
    5: <HdrAutoRoundedIcon sx={{ color: 'text.primary', mr: 1 }} />, // AutoResolved
  };

  return iconMap[state] || null; // Return the icon based on the state, or null if not found
};

const alarmTypeIconMap = {
  TimeBetweenConsumption: (
    <HourglassBottomRoundedIcon sx={{ color: 'text.secondary' }} />
  ),
  ConsumptionLevel: <InfoRoundedIcon sx={{ color: 'text.secondary' }} />,
  SeepLeakage: <WaterDamageRoundedIcon sx={{ color: 'text.secondary' }} />,
  SensorDown: <SensorsOffRoundedIcon sx={{ color: 'text.secondary' }} />,
  ActivityTimer: <AvTimerRoundedIcon sx={{ color: 'text.secondary' }} />,
  ExternalSensorDown: (
    <SensorsOffRoundedIcon sx={{ color: 'text.secondary' }} />
  ),
  ThresholdLimit: (
    <DataThresholdingRoundedIcon sx={{ color: 'text.secondary' }} />
  ),
  FastSensorDown: <SensorsOffRoundedIcon sx={{ color: 'text.secondary' }} />,
  BurstLeakage: <WaterDamageRoundedIcon sx={{ color: 'text.secondary' }} />,
  TemperatureDeviation: (
    <DeviceThermostatRoundedIcon sx={{ color: 'text.secondary' }} />
  ),
  HeatingSystem: <HeatPumpRoundedIcon sx={{ color: 'text.secondary' }} />,
  VentilationSystem: <HvacRoundedIcon sx={{ color: 'text.secondary' }} />,
  CoolingSystem: <AcUnitRoundedIcon sx={{ color: 'text.secondary' }} />,
  BillingMeter: <ReceiptLongRoundedIcon sx={{ color: 'text.secondary' }} />,
  PowerDisaggregation: (
    <ElectricalServicesRoundedIcon sx={{ color: 'text.secondary' }} />
  ),
  LowBattery: <BatteryCharging20RoundedIcon sx={{ color: 'text.secondary' }} />,
};

const AlarmDataGrid = ({
  alarms,
  alarmGroups,
  severityFilter,
  onSeverityFilterChange,
  stateFilter,
  alarmTypeFilter,
  onAlarmTypeFilterChange,
  onStateFilterChange,
  dateRange,
  onDateRangeChange,
  onToggleSeverity,
  groupedCounts,
  handleAlarmUpdate,
  showGraphs,
  setOpenCreateAlarmGroupDialog,
  setOpenEditAlarmGroupDialog,
  isLoading,
  useFeed,
  setUseFeed,
}) => {
  const theme = useTheme();

  const [dataGridState, setDataGridState] = useLocalStorageJson(
    `Alarm-DataGrid-Presets`,
    {
      presets: [
        {
          name: 'Default',
          visibilityList: {},
          density: 'standard',
          sortModel: [],
          filterModel: { items: [], quickFilterValues: [] },
        },
      ],
      chosenPreset: 0,
    },
  );

  const updateChosenPreset = (patch) =>
    setDataGridState((prev) => ({
      ...prev,
      presets: prev.presets.map((preset, idx) =>
        idx === prev.chosenPreset ? { ...preset, ...patch } : preset,
      ),
    }));

  const handleVisibilityChange = (model) => {
    updateChosenPreset({ visibilityList: model });
  };

  const handleDensityChange = (newDensity) => {
    updateChosenPreset({ density: newDensity });
  };

  const handleSortModelChange = (newModel) => {
    updateChosenPreset({ sortModel: newModel });
  };

  const handleFilterModelChange = (newModel) => {
    updateChosenPreset({ filterModel: newModel });
  };

  const currentPreset = dataGridState.presets[dataGridState.chosenPreset];

  const availableAlarmTypes = React.useMemo(() => {
    if (!alarms) return [];
    return Array.from(new Set(alarms.map((a) => a.AlarmType))).sort();
  }, [alarms]);

  const availableAlarmStates = React.useMemo(() => {
    if (!alarms) return [];
    return Array.from(new Set(alarms.map((a) => a.State))).sort();
  }, [alarms]);

  const [openAlarmDetailDialog, setOpenAlarmDetailDialog] =
    React.useState(false);
  const [selectedAlarm, setSelectedAlarm] = React.useState(null);

  const gridApiRef = useGridApiRef();

  const ASSOCIATION_ORDER = [
    'Units',
    'Buildings',
    'JobSubscriptions',
    'Sensors',
    'Gateways',
  ];

  const formatAssociations = (assoc) => {
    if (!assoc || typeof assoc !== 'object') return '';
    return ASSOCIATION_ORDER.filter(
      (k) => Array.isArray(assoc[k]) && assoc[k].length,
    )
      .map((k) => `${k}: ${assoc[k].join(', ')}`)
      .join(' → ');
  };

  const columns = [
    {
      field: 'Severity',
      headerName: 'Severity',
      width: 75,
      maxWidth: 75,
      minWidth: 75,
      sortable: false,
      renderCell: (params) => (
        <Box
          className={`severity-cell severity-${params.value?.toLowerCase()}`}
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Tooltip title={params.value} placement="right">
            <Box
              className="severity-icon"
              sx={{
                zIndex: 1,
                opacity: 0,
                display: 'flex',
                transition: 'opacity 0.5s ease',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {getSeverityIcon(params.value, theme)}
            </Box>
          </Tooltip>
          <Box
            className="severity-fill"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: '6px',
              transition: 'width 0.5s ease',
              zIndex: 0,
            }}
          />
        </Box>
      ),
    },
    {
      field: 'Title',
      headerName: 'Title',
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            // justifyContent: 'center',
            alignItems: 'center',
            height: '100%', // 🔑 Make the Box fill the DataGrid cell
            width: '100%', // optional, to help with horizontal centering
          }}
        >
          {alarmTypeIconMap[params.row.AlarmType]}
          <Typography
            variant="body2"
            noWrap
            sx={{ ml: 1, fontWeight: 'inherit' }} // ⬅️ inherit from the row
          >
            {params.value || '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'Created',
      headerName: 'Created At',
      flex: 1,
      sortComparator: (v1, v2, param1, param2) => {
        return (
          new Date(param2.row?.createdRaw) - new Date(param1.row?.createdRaw)
        ); // descending
      },
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" noWrap>
            {formatDanishDate(params.row?.createdRaw) || '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'AlarmType',
      headerName: 'Type',
      flex: 1,
    },
    {
      field: 'Associations',
      headerName: 'Associations',
      flex: 2,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const text = formatAssociations(params?.row?.Associations) || '-';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" noWrap>
              {text}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'State',
      headerName: 'State',
      width: 220,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%', // 🔑 Make the Box fill the DataGrid cell
            width: '100%', // optional, to help with horizontal centering
          }}
        >
          <FormControl sx={{ minWidth: 200 }}>
            <Select
              labelId="alarm-state-select-label"
              value={alarmStateValueMap[params.value] || 0}
              variant="outlined"
              size="small"
              sx={{
                borderRadius: 2,
                borderColor: 'divider',
              }}
              disableUnderline
              onChange={(event) =>
                handleAlarmUpdate(params.row.AlarmId, {
                  State: event.target.value,
                })
              }
            >
              {alarmStates.map((state) => (
                <MenuItem
                  key={state.value}
                  value={state.value}
                  sx={{ minWidth: 100 }}
                  disabled={state.value === 5}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getAlarmStateIcon(state.value)}
                    {state.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      ),
    },
  ];

  const rows = alarms.map((alarm) => ({
    id: alarm.AlarmId, // required by DataGrid
    createdRaw: alarm.Created, // raw ISO date
    Created: DateTime.fromISO(alarm.Created).toLocaleString(
      DateTime.DATETIME_MED_WITH_SECONDS,
    ),
    Associations: alarm.Associations,
    ...alarm,
  }));

  return (
    <Box
      sx={{
        height: showGraphs ? '58vh' : '88vh',
        width: '100%',
        transition: 'height 0.5s ease',
      }}
    >
      <DataGridPremium
        apiRef={gridApiRef}
        rows={rows}
        columns={columns}
        columnHeaderHeight={36}
        pageSize={10}
        loading={isLoading}
        onRowDoubleClick={(params) => {
          setSelectedAlarm(params.row);
          setOpenAlarmDetailDialog(true);
        }}
        rowsPerPageOptions={[10, 25, 50]}
        columnVisibilityModel={currentPreset.visibilityList}
        onColumnVisibilityModelChange={handleVisibilityChange}
        onDensityChange={(density) => handleDensityChange(density)}
        density={currentPreset.density}
        sortModel={currentPreset.sortModel}
        onSortModelChange={handleSortModelChange}
        filterModel={currentPreset.filterModel}
        onFilterModelChange={handleFilterModelChange}
        disableRowSelectionOnClick
        localeText={daDK.components.MuiDataGrid.defaultProps.localeText}
        slots={{
          toolbar: () => (
            <AlarmDataGridToolbar
              severityFilter={severityFilter}
              onSeverityFilterChange={onSeverityFilterChange}
              stateFilter={stateFilter}
              onStateFilterChange={onStateFilterChange}
              availableAlarmStates={availableAlarmStates}
              dateRange={dateRange}
              onDateRangeChange={onDateRangeChange}
              onToggleSeverity={onToggleSeverity}
              groupedCounts={groupedCounts}
              alarmTypeFilter={alarmTypeFilter}
              onAlarmTypeFilterChange={onAlarmTypeFilterChange}
              availableAlarmTypes={availableAlarmTypes}
              setOpenCreateAlarmGroupDialog={setOpenCreateAlarmGroupDialog}
              setOpenEditAlarmGroupDialog={setOpenEditAlarmGroupDialog} // Not used here, but can be passed if needed
              alarmGroups={alarmGroups}
              useFeed={useFeed}
              setUseFeed={setUseFeed}
              dataGridState={dataGridState}
              setDataGridState={setDataGridState}
            />
          ),
        }}
        slotProps={{
          loadingOverlay: {
            variant: 'linear-progress',
            nowRowsVariant: 'skeleton',
          },
          toolbar: {
            dataGridState: dataGridState,
            setDataGridState: setDataGridState,
          },
        }}
        sx={{
          border: 'none',
          '& .MuiDataGrid-cell[data-field="Severity"]': {
            paddingLeft: 0,
            marginLeft: 0,
          },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
            {
              outline: 'none',
            },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
          },
          '& .MuiDatagrid-root': {
            border: 0,
          },
          '.MuiDataGrid-withBorderColor': {
            border: 'none !important',
          },
          '& .MuiDatagrid-footerContainer': {
            border: 0,
          },
          '& .MuiDatagrid-bottomContainer': {
            border: 0,
          },
          '& .MuiDataGrid-main': {
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? '#fff' : '#121212',
            borderRadius: 2,
            border: (theme) => `1px solid ${theme.palette.divider}`,
          },
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: '1px solid', // Optional: Add a border
            borderColor: 'divider',
          },
          '& .MuiDataGrid-columnsContainer': {
            backgroundColor: 'blue',
          },
          '& .selected-row': {
            backgroundColor: (theme) => theme.palette.action.selected,
            '&:hover': {
              backgroundColor: (theme) => theme.palette.action.selected,
            },
          },
          '& .MuiDataGrid-scrollbar': {
            overflow: 'hidden !important',
          },
          '& .MuiDataGrid-cell.severity-cell': {
            padding: 0,
          },

          '& .severity-critical .severity-fill': {
            backgroundColor: '#c72929',
          },
          '& .severity-major .severity-fill': {
            backgroundColor: '#c72929',
          },
          '& .severity-medium .severity-fill': {
            backgroundColor: '#f47f1f',
          },
          '& .severity-minor .severity-fill': {
            backgroundColor: '#ffb300',
          },
          '& .severity-trivial .severity-fill': {
            backgroundColor: '#4cae4f',
          },
          '& .severity-information .severity-fill': {
            backgroundColor: '#1975d2',
          },
          '& .severity-unknown .severity-fill': {
            backgroundColor: 'transparent',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },

          '& .MuiDataGrid-row:hover .severity-fill': {
            width: '100%',
          },
          '& .MuiDataGrid-row:hover .severity-icon': {
            opacity: 1,
          },
          '& .MuiDataGrid-row.row-expanded .MuiDataGrid-cell': {
            fontWeight: 'bold', // ①  bold text
          },
          '& .MuiDataGrid-row.row-expanded .severity-fill': {
            width: '100%', // ②  keep the bar stretched
          },
          '& .MuiDataGrid-row.row-expanded .severity-icon': {
            opacity: 1, // ③  keep the icon visible
          },
        }}
      />
      <ViewAlarmDialog
        open={openAlarmDetailDialog}
        onClose={() => setOpenAlarmDetailDialog(false)}
        alarm={selectedAlarm}
      />
    </Box>
  );
};

export default AlarmDataGrid;
